import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { horizontalCap, pyramidOverRing, ringPoints } from '../solids';
import { buildVessel } from './churchVessel';
import { buildConventRange } from './conventRange';
import { buildConventEnclosure } from './conventEnclosure';
import { buildConventSoil } from './conventSoil';
import type { LandmarkContext } from './landmarkFeature';
import { centredWindow, rectWall } from './masonry';
import {
  magdalenaBarbaraChapel,
  magdalenaBarbaraFloors,
  magdalenaBarbaraFrame,
  magdalenaBarbaraRangeFrame,
  magdalenaBarbaraReading,
} from './magdalenaBarbaraPlan';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Two separate source-read convents and their closely spaced chapels. */
export function buildMagdalenaBarbara(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = magdalenaBarbaraFrame(landmark),
    frame = meshFrameOf(plan),
    reading = magdalenaBarbaraReading(landmark),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = magdalenaBarbaraChapel(landmark);
  buildVessel(builder, brick, frame, chapel, {
    longWindows: { sill: 3, top: chapel.eaveY - 1.2 },
    westWindow: true,
    bay: 5.8,
  });
  const bell = reading.belfry,
    ring = ringPoints([bell.u, bell.v], bell.radius, 8, Math.PI / 8);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      frame,
      [from, to],
      [bell.foot, bell.eaves],
      centredWindow(width, 0.35, bell.eaves - bell.foot - 0.3, 0.5),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, frame, ring, bell.foot, false);
  pyramidOverRing(builder, brick, frame, ring, bell.eaves, { plan: [bell.u, bell.v], y: bell.tip });
  for (const part of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(magdalenaBarbaraRangeFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    localGround = (p: Vec2) => terrain(toLayout(plan, p)) - base;
  buildConventEnclosure(builder, brick.wall, frame, localGround, reading.walls);
  for (const floor of magdalenaBarbaraFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
